import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Mail, Eye, Download, Clock, CheckCircle, XCircle, Headphones, Filter, X, FileDown } from 'lucide-react';
import './EmailTracking.css';

const EmailTracking = () => {
  const [sentSamples, setSentSamples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  
  // Filter states
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedGenre, setSelectedGenre] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [genres, setGenres] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchSentSamples();
    fetchGenres();
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, selectedGenre, selectedCategory]);

  const fetchSentSamples = async () => {
    try {
      const params = new URLSearchParams();
      params.append('limit', '100');
      
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (selectedGenre) params.append('genre', selectedGenre);
      if (selectedCategory) params.append('category', selectedCategory);
      
      const response = await api.get(`/email/sent?${params.toString()}`);
      setSentSamples(response.data.sentSamples);
    } catch (error) {
      console.error('Error fetching sent samples:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGenres = async () => {
    try {
      const response = await api.get('/genres');
      setGenres(response.data.genres || []);
    } catch (error) {
      console.error('Error fetching genres:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/categories');
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const clearFilters = () => {
    setStartDate('');
    setEndDate('');
    setSelectedGenre('');
    setSelectedCategory('');
  };

  const getFilteredSamples = () => {
    switch (filter) {
      case 'opened':
        return sentSamples.filter(s => s.emailOpened);
      case 'listened':
        return sentSamples.filter(s => s.listened);
      case 'downloaded':
        return sentSamples.filter(s => s.downloaded);
      case 'expired':
        return sentSamples.filter(s => s.isExpired || new Date() > new Date(s.expiresAt));
      case 'pending':
        return sentSamples.filter(s => !s.emailOpened && !s.isExpired && new Date() < new Date(s.expiresAt));
      default:
        return sentSamples;
    }
  };

  const filteredSamples = getFilteredSamples();

  const getTimeRemaining = (expiresAt) => {
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diff = expiry - now;

    if (diff <= 0) return 'Expired';

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    return `${hours}h ${minutes}m`;
  };

  const getStatusIcon = (sample) => {
    if (sample.downloaded) {
      return <CheckCircle className="status-icon success" />;
    } else if (sample.isExpired || new Date() > new Date(sample.expiresAt)) {
      return <XCircle className="status-icon expired" />;
    } else if (sample.emailOpened) {
      return <Eye className="status-icon opened" />;
    } else {
      return <Clock className="status-icon pending" />;
    }
  };

  const getStatusText = (sample) => {
    if (sample.downloaded) return 'Downloaded';
    if (sample.listened) return 'Listened';
    if (sample.isExpired || new Date() > new Date(sample.expiresAt)) return 'Expired';
    if (sample.emailOpened) return 'Opened';
    return 'Sent';
  };

  const hasActiveFilters = startDate || endDate || selectedGenre || selectedCategory;

  const handleExportCSV = () => {
    const params = new URLSearchParams({
      ...(startDate && { startDate }),
      ...(endDate && { endDate }),
      ...(selectedGenre && { genre: selectedGenre }),
      ...(selectedCategory && { category: selectedCategory })
    });

    const token = localStorage.getItem('token');
    const url = `${api.defaults.baseURL}/email/export-csv?${params}`;
    
    fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
    .then(response => response.blob())
    .then(blob => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `email-tracking-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    })
    .catch(error => {
      console.error('Error downloading CSV:', error);
      alert('Error downloading CSV');
    });
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="tracking-page">
      <div className="page-header">
        <div>
          <h2>Email Tracking</h2>
          <p>{sentSamples.length} total emails sent</p>
        </div>
        <button onClick={handleExportCSV} className="export-csv-btn" title="Download email tracking data as CSV">
          <FileDown size={18} /> Export CSV
        </button>
      </div>

      <div className="filters-section">
        <div className="status-filters">
          <button
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All ({sentSamples.length})
          </button>
          <button
            className={`filter-btn ${filter === 'pending' ? 'active' : ''}`}
            onClick={() => setFilter('pending')}
          >
            <Mail size={16} />
            Pending
          </button>
          <button
            className={`filter-btn ${filter === 'opened' ? 'active' : ''}`}
            onClick={() => setFilter('opened')}
          >
            <Eye size={16} />
            Opened
          </button>
          <button
            className={`filter-btn ${filter === 'listened' ? 'active' : ''}`}
            onClick={() => setFilter('listened')}
          >
            <Headphones size={16} />
            Listened
          </button>
          <button
            className={`filter-btn ${filter === 'downloaded' ? 'active' : ''}`}
            onClick={() => setFilter('downloaded')}
          >
            <Download size={16} />
            Downloaded
          </button>
          <button
            className={`filter-btn ${filter === 'expired' ? 'active' : ''}`}
            onClick={() => setFilter('expired')}
          >
            <Clock size={16} />
            Expired
          </button>
        </div>

        <div className="advanced-filters">
          <button 
            className={`filter-toggle-btn ${hasActiveFilters ? 'active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter size={18} />
            {hasActiveFilters ? 'Filters Active' : 'Advanced Filters'}
          </button>
        </div>
      </div>

      {showFilters && (
        <div className="advanced-filters-panel">
          <div className="filter-row">
            <div className="filter-group">
              <label>Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>

            <div className="filter-group">
              <label>End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>

            <div className="filter-group">
              <label>Category</label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <option value="">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat._id} value={cat._id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>Genre</label>
              <select
                value={selectedGenre}
                onChange={(e) => setSelectedGenre(e.target.value)}
              >
                <option value="">All Genres</option>
                {genres.map((genre) => (
                  <option key={genre._id} value={genre._id}>
                    {genre.name}
                  </option>
                ))}
              </select>
            </div>

            {hasActiveFilters && (
              <div className="filter-group">
                <label>&nbsp;</label>
                <button className="clear-filters-btn" onClick={clearFilters}>
                  <X size={16} />
                  Clear Filters
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="tracking-list">
        {filteredSamples.map((item) => (
          <div key={item._id} className="tracking-card">
            <div className="tracking-status">
              {getStatusIcon(item)}
            </div>

            <div className="tracking-info">
              <div className="tracking-header">
                <h3>{item.subscriber?.email || 'Unknown'}</h3>
                <span className="status-badge">{getStatusText(item)}</span>
              </div>

              <div className="tracking-details">
                <div className="detail-item">
                  <strong>Sample:</strong> {item.sample?.title || 'Unknown'}
                </div>
                {item.sample?.category && (
                  <div className="detail-item">
                    <strong>Category:</strong> 
                    <span className="category-badge-tracking">{item.sample.category.name}</span>
                  </div>
                )}
                {item.sample?.genre && (
                  <div className="detail-item">
                    <strong>Genre:</strong> 
                    <span className="genre-badge-tracking">{item.sample.genre.name}</span>
                  </div>
                )}
                <div className="detail-item">
                  <strong>Sent:</strong> {new Date(item.sentAt).toLocaleString()}
                </div>
                <div className="detail-item">
                  <strong>Expires:</strong> {new Date(item.expiresAt).toLocaleString()}
                </div>
                {!item.isExpired && new Date() < new Date(item.expiresAt) && (
                  <div className="detail-item time-remaining">
                    <strong>Time Remaining:</strong> {getTimeRemaining(item.expiresAt)}
                  </div>
                )}
              </div>

              <div className="tracking-timeline">
                <div className={`timeline-step ${item.sentAt ? 'completed' : ''}`}>
                  <Mail size={16} />
                  <span>Sent</span>
                </div>
                <div className="timeline-line"></div>
                <div className={`timeline-step ${item.emailOpened ? 'completed' : ''}`}>
                  <Eye size={16} />
                  <span>Opened</span>
                  {item.emailOpenedAt && (
                    <small>{new Date(item.emailOpenedAt).toLocaleTimeString()}</small>
                  )}
                </div>
                <div className="timeline-line"></div>
                <div className={`timeline-step ${item.listened ? 'completed' : ''}`}>
                  <Headphones size={16} />
                  <span>Listened</span>
                  {item.listenedAt && (
                    <small>{new Date(item.listenedAt).toLocaleTimeString()}</small>
                  )}
                </div>
                <div className="timeline-line"></div>
                <div className={`timeline-step ${item.downloaded ? 'completed' : ''}`}>
                  <Download size={16} />
                  <span>Downloaded</span>
                  {item.downloadedAt && (
                    <small>{new Date(item.downloadedAt).toLocaleTimeString()}</small>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredSamples.length === 0 && (
        <div className="empty-state">
          <Mail size={64} />
          <p>No emails found for this filter</p>
        </div>
      )}
    </div>
  );
};

export default EmailTracking;



